import { Loading } from "react-daisyui"
import ErrorPage from "../../../404/ErrorPage"
import { MajorOrderPost } from "../../../../data/Types"
import BaseModal from "../../../../components/layout/BaseModal"
import { Form } from "antd"
import { useContext, useMemo, useState } from "react"
import { useAddMajorOrderMutation } from "../../api/services"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { ModalContext } from "../../../../components/contexts/ModalContext"
import { getCustomerNamesOnly, getSingleCustomerData } from "../../../Kunden/api/apiCalls"
import { useDelayWhenSearchWithPagination } from "../../../../services/functions"
import BaseInputSelection from "../../../../components/elements/BaseInputSelection"
import { InputObject } from "../../../../components/types"
import BaseInputArea from "../../../../components/elements/BaseInputArea"
import BaseInputField from "../../../../components/elements/BaseInputField"
import { GeneralQueryKeys } from "../../../../api/constants"
import { P } from "../../../../auth/permissions"
import { Authorized } from "../../../../components/authentication/Authorized"

type AddMajorOrderModalType = {
    queryKey: string
    modalId: number
}

export default function AddMajorOrderModal(props: AddMajorOrderModalType) {
    const [majorOrder, setmajorOrder] = useState<MajorOrderPost>()

    // GET all customer for search
    const [searchTerm, setsearchTerm] = useState("")
    const searchParams = useDelayWhenSearchWithPagination(searchTerm)
    const queryKeyAllCustomer = GeneralQueryKeys.CUSTOMER_NAMES_ONLY + searchParams
    const {
        data: customerData,
        error: errorCustomer,
        isLoading: isLoadingCustomer,
    } = useQuery({
        queryKey: [queryKeyAllCustomer],
        queryFn: () => getCustomerNamesOnly(searchParams),
    })
    const displayedCustomers = useMemo(() => customerData, [customerData])

    // GET single customer
    const queryKeySingleCustomer = GeneralQueryKeys.CUSTOMER_SINGLE + majorOrder?.customer_id
    const {
        data: singleCustomer,
        error: errorSingleCustomer,
        isLoading: isLoadingSingleCustomer,
    } = useQuery({
        queryKey: [queryKeySingleCustomer],
        queryFn: () =>
            getSingleCustomerData(
                majorOrder?.customer_id ?? "",
                "_id",
                "-nr,-objectInfo,-customer_id,-employee_ids,-mailReports,-s3Documents,-serviceContract,-archived,-ranking,-deleted,-costCenter,-zipExportDownloadedBy"
            ),
        enabled: !!majorOrder?.customer_id,
    })

    // NOTE maybe for the future
    // const [documents, setdocuments] = useState<
    //     {
    //         title: ""
    //         fileUpload: File
    //         uploadedByUser: true
    //     }[]
    // >()

    // all about creating the new bill
    const queryClient = useQueryClient()
    const modalCon = useContext(ModalContext)
    const { mutate: addMajorOrder } = useAddMajorOrderMutation(queryClient, props.queryKey)
    const handleNewBill = () => {
        const formData = new FormData()
        formData.append("majorOrder", JSON.stringify(majorOrder))
        // documents?.forEach((doc: any) => {
        //     if (doc) {
        //         formData.append("additionalDocuments", doc)
        //     }
        // })
        addMajorOrder(formData)
        clearFormAndModal()
    }

    // FE LOGIC
    const [form] = Form.useForm()
    const clearFormAndModal = () => {
        form.resetFields()
        setmajorOrder(undefined)
        setsearchTerm("")
        modalCon?.trigger(0)
    }

    return (
        <Authorized anyOf={[P.buchhaltung_read_write, P.buchhaltung_read_write_restricted]}>
            <BaseModal
                onClose={clearFormAndModal}
                title={"Neuen Auftrag anlegen"}
                modalId={props.modalId}
                bottomRow={
                    <div className="flex justify-end items-center">
                        <button className={`btn btn-primary`} onClick={form.submit}>
                            Abschicken
                        </button>
                    </div>
                }>
                <div className="overflow-hidden relative h-full w-full">
                    {isLoadingSingleCustomer && <Loading />}
                    {errorSingleCustomer && <ErrorPage />}
                    {!errorSingleCustomer && (
                        <>
                            <Form form={form} onFinish={handleNewBill}>
                                <Form.Item className="my-2" name={"customer_id"} rules={[{ required: true, message: "Muss einen Wert enthalten" }]}>
                                    <BaseInputSelection
                                        setId={(e) => {
                                            form.setFieldsValue({ customer_id: e })
                                            setmajorOrder((prev: any) => ({
                                                ...prev,
                                                customer_id: e,
                                            }))
                                        }}
                                        label={"Kunde *"}
                                        placeholder={"Kunde auswählen"}
                                        data={displayedCustomers as InputObject[]}
                                        onSearch={(e: string) => setsearchTerm(e)}
                                        customLoading={isLoadingCustomer}
                                        customError={errorCustomer}
                                    />
                                </Form.Item>
                                <Form.Item className="mb-2" name={"object_id"}>
                                    <BaseInputSelection
                                        setId={(e) => {
                                            form.setFieldsValue({ object_id: e })
                                            setmajorOrder((prev: any) => ({
                                                ...prev,
                                                object_id: e,
                                            }))
                                        }}
                                        label={"Objekt (optional)"}
                                        placeholder={"Objekt auswählen"}
                                        data={(singleCustomer?.objects ?? []) as InputObject[]}
                                        disabled={singleCustomer?.objects?.length === 0 || !singleCustomer?.objects}
                                    />
                                </Form.Item>
                                <Form.Item className="mb-2" name={"description"} rules={[{ required: true, message: "Muss einen Wert enthalten" }]}>
                                    <BaseInputArea
                                        label={"Beschreibung"}
                                        onChange={(e) =>
                                            setmajorOrder((prev: any) => ({
                                                ...prev,
                                                description: e,
                                            }))
                                        }
                                    />
                                </Form.Item>
                                <Form.Item className="mb-2" name={"amount"} rules={[{ required: true, message: "Muss einen Wert enthalten" }]}>
                                    <BaseInputField
                                        label={"Betrag in €"}
                                        type={"number"}
                                        onChange={(e) =>
                                            setmajorOrder((prev: any) => ({
                                                ...prev,
                                                amount: Number(e),
                                            }))
                                        }
                                    />
                                </Form.Item>
                            </Form>
                        </>
                    )}
                </div>
            </BaseModal>
        </Authorized>
    )
}
